import { useCallback } from "react";
import { v4 as uuidv4 } from "uuid";
import { wait } from "@sourceful/shared-utils/fingerprints";
import {
  ConfigurationItem,
  ConfigurationItemCreationAttrs,
  GQLResponse,
} from "@sourceful/shared-types";
import { CLAIMS_DOMAIN, useAuthentication } from "@providers/AuthenticationProvider";
import { handleAxiosError } from "@lib/handleAxiosError";
import { useGraphQLMutation } from "@lib/hooks/useGraphQLRequest";
import { useSyncConfigItem } from "./useSyncConfigItem";
import { ADD_CONFIGURATION_ITEMS } from "@gql/mutations/configuration/addConfigurationItem";
import {
  AddConfigurationItemsMutation,
  AddConfigurationItemsMutationVariables,
} from "@generated/graphql";
import {
  formatConfigurationItemFragment,
  prepareConfigurationItemCreationVars,
} from "../helpers/format-helpers";

export type CreateConfigurationItem = (
  items: ConfigurationItemCreationAttrs,
  _retries?: number,
  _maxRetries?: number
) => Promise<ConfigurationItem>;
export const useCreateConfigItem = () => {
  const [addConfigurationItems] =
    useGraphQLMutation<
      AddConfigurationItemsMutationVariables,
      GQLResponse<AddConfigurationItemsMutation>
    >(ADD_CONFIGURATION_ITEMS);

  const syncConfigItem = useSyncConfigItem();
  const { user, isLoading: isAuthLoading } = useAuthentication();

  const createConfigurationItem: CreateConfigurationItem = useCallback(
    async (
      item: ConfigurationItemCreationAttrs,
      retries: number = 0,
      maxRetries: number = 5
    ): Promise<ConfigurationItem> => {
      const userId = user?.sub || null;
      const userUuidInternal = user  ? user[CLAIMS_DOMAIN].user.uuid : null;

      try {
        console.debug("ConfigurationProviderV2.createConfigurationItem", {
          itemId: item.id,
          userId: userId,
          userUuidInternal: userUuidInternal,
          item: JSON.stringify(item),
          retries,
          maxRetries,
          isAuthLoading,
        });

        let newItem: ConfigurationItem;

        if (isAuthLoading && retries > maxRetries) {
          throw new Error(
            "ConfigurationProviderV2.createConfigurationItem: Timed out trying to auth whilst attempting to save configuration item"
          );
        } else if (isAuthLoading) {
          console.debug(
            "ConfigurationProviderV2.createConfigurationItem: Auth is loading - retrying"
          );
          await wait(1000);
          return createConfigurationItem(item, retries + 1);
        }

        if (!user || !user?.sub) {
          // guest basket
          newItem = {
            id: `G-${uuidv4()}`,
            revisions: [],
            ...item,
          };
        } else {
          console.debug("ConfigurationProviderV2.createConfigurationItem: creating...");

          const itemPayloads = prepareConfigurationItemCreationVars([item]);
          const res = await addConfigurationItems({
            objects: itemPayloads,
          });

          const newItems = res?.data?.insert_product_cloud_configuration_item?.returning || [];

          if (!newItems?.length) {
            console.error("An error occurred while creating configuration item", {
              item,
            });
            throw new Error("An error occurred while creating configuration item");
          }

          newItem = formatConfigurationItemFragment(newItems[0], item.product, item.preset);
        }

        await syncConfigItem(
          newItem, 
          {
            syncGuestItem: true,
          },
        );

        return newItem;
      } catch (error) {
        handleAxiosError(error);
        throw error;
      }
    },
    [addConfigurationItems, isAuthLoading, syncConfigItem, user]
  );

  return createConfigurationItem;
};
